/**
 * How many writes the data layer still owes the server, app-wide — every
 * item every `documentWriteQueue` holds, in flight, waiting for its batch or
 * waiting out a retry — and a subscription on the count.
 *
 * What a "not saved" indicator reads (`hooks/useUnsavedWrites`): an edit is
 * shown before its round trip, and with retries a tunnel turns a lost edit
 * into a delayed one, so the user needs to know while anything is still
 * outstanding. Counted here rather than per store so one badge covers the
 * structures session and every room's queue.
 */
const listeners = new Set<(count: number) => void>();
let outstanding = 0;

/**
 * While anything is outstanding, closing or reloading the tab asks first
 * (the browser's own "Leave site?" prompt): the queued writes live in this
 * page and go with it. Owned here, with the count, so every page gets it —
 * not only the ones that mount the badge.
 */
const askBeforeUnload = (event: BeforeUnloadEvent) => {
  event.preventDefault();
  // older browsers show the prompt only when returnValue is set
  event.returnValue = '';
};
let guarding = false;
const syncLeaveGuard = () => {
  if (typeof window === 'undefined') return;
  const shouldGuard = outstanding > 0;
  if (shouldGuard === guarding) return;
  guarding = shouldGuard;
  if (shouldGuard) window.addEventListener('beforeunload', askBeforeUnload);
  else window.removeEventListener('beforeunload', askBeforeUnload);
};

const publish = () => {
  syncLeaveGuard();
  listeners.forEach((l) => l(outstanding));
};

/** A queue reports items taken on (+n) and settled (-n). */
export function trackPendingWrites(delta: number): void {
  const next = Math.max(0, outstanding + delta);
  if (next === outstanding) return;
  outstanding = next;
  publish();
}

export const pendingWriteCount = (): number => outstanding;

export function subscribePendingWrites(
  listener: (count: number) => void
): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** For tests: is the leave-page prompt armed right now? */
export const __leaveGuardArmedForTests = (): boolean => guarding;

export const __resetPendingWritesForTests = (): void => {
  outstanding = 0;
  listeners.clear();
  syncLeaveGuard();
};
